import { SubmitHandler, UseFormReturn } from "react-hook-form";

import { CustomerStatus, ICustomer } from "../../models/Customer/Customer";
import { enumToSelectOptions } from "../../utils/enumToKeyValue";
import Form from "../base/Form/Form";
import { FormErrors } from "../base/Form/FormErrors";
import { FormSelect } from "../base/Form/FormSelect";
import { FormTextField } from "../base/Form/FormTextField";
import Card from "../shared/Card/Card";

export interface BasicInformationFormFields {
  id: string;
  name: string;
  usernamePrefix: string;
  domain: string;
  frontEndDomain: string;
  status: string;
  line1: string;
  line2: string;
  line3: string;
  line4: string;
  locality: string;
  town: string;
  county: string;
  postcode: string;
  vatNumber: string;
  email: string;
  widgetEmailDisplayEmail: string;
  contactName: string;
  smtpFrom: string;
  smtpFromName: string;
  telephone: string;
}

interface BasicInformationProps {
  customer: ICustomer;
  onSubmit: SubmitHandler<BasicInformationFormFields>;
  onReady: (methods: UseFormReturn<BasicInformationFormFields>) => void;
}

export function EditBasicInformation({
  customer,
  onSubmit,
  onReady,
}: BasicInformationProps) {

  return (
    <Card title="Basic Information">
      <Form<BasicInformationFormFields>
        onSubmit={onSubmit}
        onReady={onReady}
        defaultValues={{
          ...customer,
          status: String(customer.status),
        }}
      >
        {({ register }: UseFormReturn<BasicInformationFormFields>) => (
          <>
            <FormErrors />
            <input type="hidden" {...register("id")} />
            <dl className="px-4 py-2 divide-y divide-gray-100">
              <div className="grid grid-cols-1 md:grid-cols-3 py-2">
                <dt className="text-sm font-medium leading-6 text-gray-900">
                  Name
                </dt>
                <dd className="mt-1 text-sm leading-6 text-gray-700 md:col-span-2 md:mt-0">
                  <FormTextField
                    placeholder="Wilma Co Ltd."
                    {...register("name", { required: true })}
                  />
                </dd>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 py-2">
                <dt className="text-sm font-medium leading-6 text-gray-900">
                  Username Prefix
                </dt>
                <dd className="mt-1 text-sm leading-6 text-gray-700 md:col-span-2 md:mt-0">
                  <FormTextField
                    placeholder="wilma"
                    {...register("usernamePrefix", { required: true })}
                  />
                </dd>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 py-2">
                <dt className="text-sm font-medium leading-6 text-gray-900">
                  Domain
                </dt>
                <dd className="mt-1 text-sm leading-6 text-gray-700 md:col-span-2 md:mt-0">
                  <FormTextField
                    placeholder="wilma.recyclingsoftware.com"
                    {...register("domain", { required: true })}
                  />
                </dd>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 py-2">
                <dt className="text-sm font-medium leading-6 text-gray-900">
                  Front-End Domain
                </dt>
                <dd className="mt-1 text-sm leading-6 text-gray-700 md:col-span-2 md:mt-0">
                  <FormTextField
                    placeholder="wilma.recyclingsoftware.com"
                    {...register("frontEndDomain", { required: true })}
                  />
                </dd>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 py-2">
                <dt className="text-sm font-medium leading-6 text-gray-900">
                  Status
                </dt>
                <dd className="mt-1 text-sm leading-6 text-gray-700 md:col-span-2 md:mt-0">
                  <FormSelect {...register("status")}>
                    {enumToSelectOptions(CustomerStatus).map((status) => (
                      <option value={status.value}>{status.label}</option>
                    ))}
                  </FormSelect>
                </dd>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 py-2">
                <dt className="text-sm font-medium leading-6 text-gray-900">
                  Address
                </dt>
                <dd className="mt-1 text-sm leading-6 text-gray-700 space-y-2 md:col-span-2 md:mt-0">
                  <FormTextField placeholder="Line 1" {...register("line1")} /> 
                  <FormTextField placeholder="Line 2" {...register("line2")} />
                  <FormTextField placeholder="Line 3" {...register("line3")} />
                  <FormTextField placeholder="Line 4" {...register("line4")} />
                  <FormTextField placeholder="Locality" {...register("locality")} />
                  <FormTextField placeholder="Town" {...register("town")} />
                  <FormTextField placeholder="County" {...register("county")} />
                  <FormTextField placeholder="Postcode" {...register("postcode")} />
                </dd>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 py-2">
                <dt className="text-sm font-medium leading-6 text-gray-900">
                  Email
                </dt>
                <dd className="mt-1 text-sm leading-6 text-gray-700 md:col-span-2 md:mt-0">
                  <FormTextField
                    placeholder="Email"
                    {...register("email", { required: false })}
                  />
                </dd>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 py-2">
                <dt className="text-sm font-medium leading-6 text-gray-900">
                  Widget Display Email
                </dt>
                <dd className="mt-1 text-sm leading-6 text-gray-700 md:col-span-2 md:mt-0">
                  <FormTextField
                    placeholder="Widget Display Email"
                    {...register("widgetEmailDisplayEmail", { required: false })}
                  />
                </dd>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 py-2">
                <dt className="text-sm font-medium leading-6 text-gray-900">
                  Contact Name
                </dt>
                <dd className="mt-1 text-sm leading-6 text-gray-700 md:col-span-2 md:mt-0">
                  <FormTextField
                    placeholder="Contact Name"
                    {...register("contactName", { required: false })}
                  />
                </dd>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 py-2">
                <dt className="text-sm font-medium leading-6 text-gray-900">
                  SMTP From
                </dt>
                <dd className="mt-1 text-sm leading-6 text-gray-700 md:col-span-2 md:mt-0">
                  <FormTextField
                    placeholder="SMTP From"
                    {...register("smtpFrom", { required: false })}
                  />
                </dd>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 py-2">
                <dt className="text-sm font-medium leading-6 text-gray-900">
                  SMTP From Name
                </dt>
                <dd className="mt-1 text-sm leading-6 text-gray-700 md:col-span-2 md:mt-0">
                  <FormTextField
                    placeholder="SMTP From Name"
                    {...register("smtpFromName", { required: false })}
                  />
                </dd>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 py-2">
                <dt className="text-sm font-medium leading-6 text-gray-900">
                  Telephone
                </dt>
                <dd className="mt-1 text-sm leading-6 text-gray-700 md:col-span-2 md:mt-0">
                  <FormTextField
                    placeholder="Wilma Co Ltd."
                    {...register("telephone", { required: false })}
                  />
                </dd>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 py-2">
                <dt className="text-sm font-medium leading-6 text-gray-900">
                  VAT Number
                </dt>
                <dd className="mt-1 text-sm leading-6 text-gray-700 md:col-span-2 md:mt-0">
                  <FormTextField
                    placeholder="VAT Number"
                    {...register("vatNumber", { required: false })}
                  />
                </dd>
              </div>
            </dl>
          </>
        )}
      </Form>
    </Card>
  );
}
